import { useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  School,
  FileText,
  ListTree,
  DollarSign,
  Stars,
  UserCheck,
  Calendar,
  UserCircle,
  LogOut,
  Home,
  Search,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { UserAuth } from "../../context/AuthContext";
import { UserProfile } from "../../context/ProfileContext";
import { AvatarDisplay } from "../student/profile/AvatarDisplay";
import NotificationBell from "./NotificationBell";
import { useOverflow } from "../../hooks/useOverflow";

interface StudentSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function StudentSidebar({
  collapsed = false,
  onToggleCollapse,
}: StudentSidebarProps) {
  const navigate = useNavigate();
  const { signOut } = UserAuth();
  const profileContext = UserProfile();
  const navListRef = useRef<HTMLUListElement>(null);
  const navOverflow = useOverflow(navListRef);

  // Safely destructure with defaults if context is null
  const { profile = null, photo = { avatarUrl: null, initials: "LP" } } =
    profileContext || {};

  const { first_name = "Student", last_name = "Name" } = profile || {};

  const navItems = [
    { to: "/student", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/student/colleges", label: "Colleges", icon: School },
    { to: "/student/essays", label: "Essays", icon: FileText },
    { to: "/student/supplements", label: "Supplements", icon: ListTree },
    { to: "/student/activities", label: "Activities", icon: ListTree },
    { to: "/student/financial-aid", label: "Financial Aid", icon: DollarSign },
    { to: "/student/rate-my-chance", label: "Rate My Chance", icon: Stars },
    { to: "/student/research", label: "Research", icon: Search },
    { to: "/student/chat", label: "Chat", icon: MessageSquare },
    { to: "/student/counselor", label: "My Counselor", icon: UserCheck },
    { to: "/student/calendar", label: "Calendar", icon: Calendar },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.log("error signing out occurred", error);
    }
  };

  return (
    <nav
      data-overflow={navOverflow}
      className={`sidebar-nav-root flex flex-col h-full bg-white dark:bg-black/45 border border-gray-200 dark:border-violet-500/15 rounded-3xl backdrop-blur-xl shadow-[0_35px_80px_-60px_rgba(120,90,200,0.5)] transition-[padding] duration-300 ${
        collapsed ? "px-3 items-center" : "px-6"
      }`}
    >
      {/* Logo/Brand — flex: 0 0 auto zone, never shrinks below its content */}
      <div className="sidebar-brand-block w-full flex flex-col mt-2 shrink-0">
        <button
          onClick={() => navigate("/")}
          className={`flex items-center ${
            collapsed ? "justify-center" : "space-x-2"
          } text-gray-400 hover:text-black dark:hover:text-white transition-colors duration-150 group w-full`}
          title={collapsed ? "Back to Home" : undefined}
        >
          <Home className="w-5 h-5 flex-shrink-0 group-hover:-translate-x-1 transition-transform duration-200" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                layout
                className="text-sm scroll-m-0 whitespace-nowrap overflow-hidden"
              >
                Back to Home
              </motion.span>
            )}
          </AnimatePresence>
        </button>
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              layout
              className="overflow-hidden"
            >
              <div className="sidebar-brand-divider mt-4 border-t border-violet-500/20">
                <h2 className="text-xl font-semibold bg-gradient-to-r from-violet-700 to-violet-400 dark:from-white dark:via-violet-200 dark:to-violet-400 bg-clip-text text-transparent whitespace-nowrap">
                  Student Portal
                </h2>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation Items — the only zone allowed to scroll. min-h-0 overrides
          the flex default of min-height:auto (= min-content height), which is
          what let this <ul> refuse to shrink and push the bottom block
          off-screen at reduced window heights. */}
      <ul
        ref={navListRef}
        className={`sidebar-nav-scroll sidebar-nav-list flex-1 min-h-0 overflow-y-auto w-full pt-2 ${
          collapsed ? "px-1" : ""
        }`}
      >
        {navItems.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive
                  ? `sidebar-nav-button sidebar-nav-row flex items-center ${collapsed ? "justify-center" : "gap-3"} w-full px-5 rounded-[24px] text-sm font-medium transition-shadow transition-colors duration-200 overflow-hidden border bg-violet-100 text-violet-800 border-violet-200 shadow-sm dark:bg-[#1a0f45] dark:border-[#c9a9ff] dark:text-white dark:shadow-[0_0_10px_rgba(167,128,255,0.65)] group`
                  : `sidebar-nav-button sidebar-nav-row group flex items-center ${collapsed ? "justify-center" : "gap-3"} w-full px-5 rounded-[24px] text-sm font-medium transition-shadow transition-colors duration-200 overflow-hidden border bg-white text-gray-700 border-gray-200 shadow-none dark:bg-[#050309] dark:border-[#3f3277] dark:text-[#d6cfff] hover:bg-[#ede9fe] dark:hover:bg-[#130b33] hover:border-gray-300 dark:hover:border-[#b193ff] hover:text-gray-900 dark:hover:text-white hover:shadow-[0_0_6px_rgba(167,128,255,0.5)]`
              }
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="h-4 w-4 flex-shrink-0 opacity-90 group-hover:opacity-100" />
              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    layout
                    className="whitespace-nowrap overflow-hidden"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
              {collapsed && (
                <span className="absolute left-full ml-3 px-3 py-1.5 rounded-lg bg-black/80 border border-white/10 text-xs text-white opacity-0 group-hover:opacity-100 pointer-events-none shadow-xl whitespace-nowrap">
                  {item.label}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>

      {/* Bottom Section - Profile & Logout — flex: 0 0 auto zone, never
          scrollable, never clipped */}
      <div
        className={`sidebar-bottom-block shrink-0 w-full ${
          collapsed ? "items-center" : ""
        }`}
      >
        <NavLink
          to="/student/profile"
          className={({ isActive }) =>
            isActive
              ? `sidebar-nav-button sidebar-nav-row flex items-center ${collapsed ? "justify-center" : "gap-3"} w-full px-5 rounded-[24px] text-sm font-medium transition-shadow transition-colors duration-200 overflow-hidden border bg-violet-100 text-violet-800 border-violet-200 shadow-sm dark:bg-[#1a0f45] dark:border-[#c9a9ff] dark:text-white dark:shadow-[0_0_10px_rgba(167,128,255,0.65)] group`
              : `sidebar-nav-button sidebar-nav-row group flex items-center ${collapsed ? "justify-center" : "gap-3"} w-full px-5 rounded-[24px] text-sm font-medium transition-shadow transition-colors duration-200 overflow-hidden border bg-white text-gray-700 border-gray-200 shadow-none dark:bg-[#050309] dark:border-[#3f3277] dark:text-[#d6cfff] hover:bg-[#ede9fe] dark:hover:bg-[#130b33] hover:border-gray-300 dark:hover:border-[#b193ff] hover:text-gray-900 dark:hover:text-white hover:shadow-[0_0_6px_rgba(167,128,255,0.5)]`
          }
        >
          {collapsed ? (
            <UserCircle className="w-5 h-5 flex-shrink-0 opacity-90 group-hover:opacity-100" />
          ) : (
            <AvatarDisplay
              avatarUrl={photo?.avatarUrl}
              initials={photo?.initials || "LP"}
              size="sm"
            />
          )}
          <AnimatePresence>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                layout
                className="flex-1 min-w-0 overflow-hidden"
              >
                <p className="truncate whitespace-nowrap text-sm font-semibold">
                  {first_name} {last_name}
                </p>
                <p className="text-xs text-gray-500 whitespace-nowrap">
                  View Profile
                </p>
              </motion.div>
            )}
          </AnimatePresence>
          {collapsed && (
            <span className="absolute left-full ml-3 px-3 py-1.5 rounded-lg bg-black/80 border border-white/10 text-xs text-white opacity-0 group-hover:opacity-100 pointer-events-none shadow-xl whitespace-nowrap">
              Profile
            </span>
          )}
        </NavLink>

        <NotificationBell collapsed={collapsed} />

        <button
          onClick={handleSignOut}
          className={`relative w-full flex items-center ${
            collapsed ? "justify-center" : "space-x-3"
          } px-4 py-3 rounded-xl text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-150`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                className="text-sm font-medium whitespace-nowrap overflow-hidden"
              >
                Sign Out
              </motion.span>
            )}
          </AnimatePresence>
          {collapsed && (
            <span className="absolute left-full ml-3 px-3 py-1.5 rounded-lg bg-black/80 border border-white/10 text-xs text-white opacity-0 group-hover:opacity-100 pointer-events-none shadow-xl whitespace-nowrap">
              Sign Out
            </span>
          )}
        </button>

        {onToggleCollapse && (
          <button
            onClick={() => onToggleCollapse?.()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-[#A1A1AA] hover:text-white hover:border-violet-400/50 hover:bg-white/10 transition"
            aria-label="Toggle sidebar"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="whitespace-nowrap overflow-hidden"
                >
                  Collapse
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        )}
      </div>
    </nav>
  );
}

export default StudentSidebar;
